import { useState } from "react";
import { Badge, Button, Icon } from "@vavr/ui";

export interface CalendarEntry { id: string; site_id: string | null; school: string | null; from_date: string; to_date: string; kind: "no_service" | "early_release" | "late_start"; minutes: number | null; note: string | null; mine: boolean; by_name: string | null }
export const CALENDAR_KIND = { no_service: "No service", early_release: "Early release", late_start: "Late start" };
const iso = (date: Date) => date.toISOString().slice(0, 10);
const label = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

/** UTC date arithmetic keeps the service day stable across browser time zones and DST. */
export function monthDays(month: string): string[] {
  const first = new Date(`${month}-01T12:00:00Z`);
  first.setUTCDate(first.getUTCDate() - (first.getUTCDay() + 6) % 7);
  return Array.from({ length: 42 }, (_, i) => iso(new Date(first.getTime() + i * 86400000)));
}
export const entriesOn = (entries: CalendarEntry[], date: string) => entries.filter((e) => e.from_date <= date && e.to_date >= date);

export function ServiceMonth({ today, entries, onAdd }: { today: string; entries: CalendarEntry[]; onAdd: (date: string) => void }) {
  const [selected, setSelected] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  const dates = monthDays(month);
  const daily = entriesOn(entries, selected);
  const move = (offset: number) => {
    const date = new Date(`${month}-01T12:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + offset);
    const next = iso(date);
    setMonth(next.slice(0, 7)); setSelected(next < today ? today : next);
  };
  const select = (date: string) => { setSelected(date); setMonth(date.slice(0, 7)); };
  return <section className="ptr-calendar" aria-label="Upcoming service calendar">
    <div className="ptr-calendar__month">
      <header className="ptr-calendar__head">
        <h3 aria-live="polite">{new Date(`${month}-01T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })}</h3>
        <div className="pmu-row">
          <Button variant="ghost" size="sm" icon="chevron-left" aria-label="Previous month" disabled={month <= today.slice(0, 7)} onClick={() => move(-1)} />
          <Button variant="secondary" size="sm" onClick={() => select(today)}>Today</Button>
          <Button variant="ghost" size="sm" icon="chevron-right" aria-label="Next month" onClick={() => move(1)} />
        </div>
      </header>
      <table className="ptr-calendar__grid" aria-label="Choose a service day">
        <thead><tr>{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <th scope="col" key={d}>{d}</th>)}</tr></thead>
        <tbody>{Array.from({ length: 6 }, (_, week) => <tr key={week}>{dates.slice(week * 7, week * 7 + 7).map((date) => {
          const changes = entriesOn(entries, date);
          return <td key={date}><button type="button" disabled={date < today} aria-pressed={date === selected} aria-current={date === today ? "date" : undefined}
            className={`ptr-calendar__day${date.slice(0, 7) !== month ? " ptr-calendar__day--outside" : ""}`}
            aria-label={`${label(date)}${date < today ? ", past date" : `, ${changes.length} service changes`}`} onClick={() => select(date)}>
            <span>{Number(date.slice(-2))}</span>
            <span className="ptr-calendar__marks" aria-hidden="true">{Object.keys(CALENDAR_KIND).filter((kind) => changes.some((e) => e.kind === kind)).map((kind) => <i className={`ptr-calendar__mark ptr-calendar__mark--${kind}`} key={kind} />)}</span>
          </button></td>;
        })}</tr>)}</tbody>
      </table>
      <div className="ptr-calendar__legend">{Object.entries(CALENDAR_KIND).map(([key, text]) => <span key={key}><i className={`ptr-calendar__mark ptr-calendar__mark--${key}`} />{text}</span>)}</div>
      <p className="pmu-small pmu-muted">Upcoming changes only. Select a date to see affected routes and schools.</p>
    </div>
    <aside className="ptr-calendar__agenda" aria-label="Selected day" aria-live="polite">
      <span className="ptr-workspace-label">DAY DETAILS</span><h3>{label(selected)}</h3>
      {daily.length ? <ul>{daily.map((e) => <li key={e.id}>
        <Badge tone={e.kind === "no_service" ? "warning" : "info"}>{CALENDAR_KIND[e.kind]}{e.minutes ? ` · ${e.minutes} min` : ""}</Badge>
        <strong>{e.note || CALENDAR_KIND[e.kind]}</strong><span>{e.school || "All our routes"}</span>
        <small>{e.mine ? "Managed by your team" : "Set by the school · read only"}</small>
      </li>)}</ul> : <div className="ptr-calendar__empty"><Icon name="calendar" /><strong>No changes recorded</strong><p>Regular route schedules apply unless another service rule changes them.</p></div>}
      <Button variant="secondary" icon="plus" onClick={() => onAdd(selected)}>Add a change for this day</Button>
    </aside>
  </section>;
}
